<template>
  <div>
    <p class="has-margin-bottom-150">
      Hold tight – we're setting everything up for you. This will only take a
      few moments, please don't close this window.
    </p>

    <ul class="provision-list has-margin-bottom-150">
      <li
        v-for="stage in stages"
        :key="stage.key"
        class="has-margin-bottom-75"
      >
        <span class="provision-list__icon">
          <span
            v-if="stage.status === 'active'"
            class="loader"
          />
          <b-icon
            v-else-if="stage.status === 'done'"
            icon="check-circle"
            type="is-success"
          />
          <b-icon
            v-else-if="stage.status === 'error'"
            icon="alert-circle"
            type="is-danger"
          />
          <b-icon
            v-else
            icon="checkbox-blank-circle-outline"
            class="has-text-grey-light"
          />
        </span>
        <span
          :class="{
            'has-text-grey-light': stage.status === 'pending',
            'has-text-weight-semibold': stage.status === 'active'
          }"
        >{{ stage.label }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where
} from "@firebase/firestore";

export default {
  name: "GoogleCleanupProvisioningStep",
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      running: false,
      canRetry: true,
      stages: [
        { key: "prepare", label: "Preparing your account", status: "pending" },
        { key: "site", label: "Adding your website", status: "pending" },
        { key: "coupon", label: "Redeeming your coupon", status: "pending" },
        {
          key: "task",
          label: "Creating your free cleanup task",
          status: "pending"
        }
      ]
    };
  },
  created() {
    // Mark any work already completed on a previous attempt
    if (this.payload.siteId) this.setStatus("site", "done");
    if (this.payload.orderNum) this.setStatus("coupon", "done");
    if (this.payload.taskId) this.setStatus("task", "done");
    this.run();
  },
  methods: {
    setStatus(key, status) {
      const stage = this.$_.find(this.stages, { key });
      if (stage) stage.status = status;
    },
    runStage(key, fn) {
      this.setStatus(key, "active");
      // Hold each stage on screen briefly so progress is legible
      return Promise.all([
        fn(),
        new Promise(resolve => setTimeout(resolve, 660))
      ])
        .then(([result]) => {
          this.setStatus(key, "done");
          return result;
        })
        .catch(error => {
          this.setStatus(key, "error");
          throw error;
        });
    },
    async run() {
      if (this.running) return;
      this.running = true;
      this.canRetry = true;
      try {
        const { productId, productTermId, taskTypePath } = await this.runStage(
          "prepare",
          () => this.prepare()
        );

        let siteId = this.payload.siteId;
        if (!siteId) {
          siteId = await this.runStage("site", () => this.addSite());
          this.$emit("payload", { siteId });
        }

        let orderNum = this.payload.orderNum;
        if (!orderNum) {
          orderNum = await this.runStage("coupon", () =>
            this.placeOrder({ siteId, productId, productTermId })
          );
          this.$emit("payload", { orderNum });
        }

        let taskId = this.payload.taskId;
        if (!taskId) {
          taskId = await this.runStage("task", () =>
            this.addTask({ siteId, taskTypePath })
          );
          this.$emit("payload", { taskId });
        }

        setTimeout(() => {
          this.$emit("next");
        }, 900);
      } catch (error) {
        const message =
          (this.$_.isString(error) ? error : error && error.message) ||
          "Oops, something went wrong – please try again.";
        /* All errors surface in a modal: recoverable ones offer a retry,
        unrecoverable ones route back to the first step. Either way the user
        always keeps a way out – a retry that never succeeds (eg. the user
        document never appears) would otherwise strand them here. */
        const canRetry = this.canRetry;
        this.$modal.open({
          component: () => import("@shared/ui/_noticeModal"),
          parent: this,
          width: 500,
          hasModalCard: true,
          canCancel: canRetry ? false : undefined,
          props: {
            title: canRetry
              ? "Something went wrong"
              : "We couldn't complete your setup",
            message,
            actionText: canRetry ? "Try again" : "Start over",
            dismissText: canRetry ? "Start over" : null
          },
          events: {
            action: () => (canRetry ? this.run() : this.$emit("startOver")),
            dismiss: () => this.$emit("startOver")
          },
          onCancel: () => this.$emit("startOver")
        });
      } finally {
        this.running = false;
      }
    },
    async prepare() {
      // 1. Wait for the user document (created by a backend trigger shortly
      // after signup) – the order endpoints require it to exist.
      const userRef = doc(this.$firestore, `users/${this.payload.userId}`);
      let userExists = false;
      for (let attempt = 0; attempt < 40; attempt++) {
        const user = await getDoc(userRef);
        if (user.exists()) {
          userExists = true;
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 750));
      }
      if (!userExists) {
        this.canRetry = true;
        throw "We couldn't finish preparing your account. Please try again in a moment.";
      }

      // 2. Resolve the one-time task product & term. Prefer the term the
      // promotion is restricted to (if any), otherwise fall back to the
      // active one-time task-credit product.
      let productId = null;
      let productTermId = null;
      try {
        const coupon = await getDoc(
          doc(this.$firestore, `coupons/${this.payload.couponId}`)
        );
        const promotion = await getDoc(coupon.data().promotionRef);
        const eligibleTerms = promotion.data().eligibleProductTerms || [];
        if (eligibleTerms.length) {
          const termRef = eligibleTerms[0];
          productTermId = termRef.id;
          productId = termRef.parent.parent.id;
        }
      } catch (error) {
        /* Usually just means the coupon carries no term restriction, but a
        genuine read failure looks identical here – log it, because falling
        back to the default term when the coupon is actually scoped to another
        one surfaces later as a confusing "coupon doesn't cover the cost". */
        console.error("Could not resolve promotion product term:", error);
      }
      if (!productId) {
        const products = await getDocs(
          query(
            collection(this.$firestore, "products"),
            where("type", "==", "taskCredit"),
            where("isActive", "==", true)
          )
        );
        let product = null;
        products.forEach(p => {
          if (!product || (p.data().order || 0) < (product.data().order || 0))
            product = p;
        });
        if (!product) {
          this.canRetry = false;
          throw "Sorry, the one-time task product isn't currently available. Please contact our team and we'll get you set up.";
        }
        const terms = await getDocs(
          query(collection(product.ref, "terms"), where("isActive", "==", true))
        );
        const currency = this.payload.currency || "gbp";
        let term = null;
        terms.forEach(t => {
          const price = this.$_.get(t.data(), `termPrice.${currency}`, 0);
          const best = term
            ? this.$_.get(term.data(), `termPrice.${currency}`, 0)
            : null;
          if (!term || price < best) term = t;
        });
        if (!term) {
          this.canRetry = false;
          throw "Sorry, the one-time task product isn't currently available. Please contact our team and we'll get you set up.";
        }
        productId = product.id;
        productTermId = term.id;
      }

      // 3. Resolve the task type used for the automatically created task
      let taskTypePath = "taskTypes/first-task";
      try {
        const taskType = await getDoc(doc(this.$firestore, taskTypePath));
        if (taskType.exists()) taskTypePath = taskType.ref.path;
      } catch (error) {
        // Fall back to the literal path
        console.error("Could not resolve task type:", error);
      }

      return { productId, productTermId, taskTypePath };
    },
    async addSite() {
      const fqdn = this.payload.siteUrl.fqdn.toLowerCase();

      /* Guard against duplicate sites from retried or restarted runs – if
      this account already has a live site with the same domain, reuse it
      rather than creating another. */
      const userSites = await getDocs(
        query(
          collection(this.$firestore, "sites"),
          where("users", "array-contains", this.payload.userId)
        )
      );
      let existingSiteId = null;
      userSites.forEach(site => {
        const siteFqdn = (site.data().fqdn || "").toLowerCase();
        if (!site.data().isDeleted && siteFqdn === fqdn)
          existingSiteId = site.id;
      });
      if (existingSiteId) return existingSiteId;

      /* The coupon is passed (but not redeemed) so the backend can attribute
      the site to the promotion's campaign. */
      const result = await this.$store.dispatch("sites/addSite", {
        fqdn: this.payload.siteUrl.fqdn,
        url: this.payload.siteUrl.url,
        software: this.payload.siteSoftware,
        userId: this.payload.userId,
        couponId: this.payload.couponId
      });
      return result.data.siteId;
    },
    async placeOrder({ siteId, productId, productTermId }) {
      // Dry-run the basket first – the coupon must still be valid and must
      // cover the full cost, as no payment method is captured in this flow.
      const basket = await this.$store.dispatch("billing/calculateBasket", {
        userId: this.payload.userId,
        siteId,
        productId,
        productTermId,
        paymentMethodId: null,
        couponId: this.payload.couponId,
        currency: this.payload.currency
      });
      if (!this.$_.isEmpty(basket.errors)) throw basket.errors[0];
      if (this.$_.isEmpty(basket.coupons)) {
        this.canRetry = false;
        /* The public coupon doc can't tell us about per-user limits (the
        redeemers subcollection is backend-only) – so if the coupon itself
        still looks valid, the refusal must be scoped to this account. */
        const { isValid } = await this.$store.dispatch(
          "promotion/validateCoupon",
          { couponId: this.payload.couponId }
        );
        if (isValid)
          throw "It looks like your account has already redeemed this offer, which is limited to one per customer. Please contact our team if you think something's wrong.";
        throw "Sorry, it looks like your coupon code is no longer valid or has already been used. Please contact our team if you think something's wrong.";
      }
      if (basket.total > 0) {
        this.canRetry = false;
        throw "Sorry, your coupon doesn't fully cover the cost of this task, so we couldn't complete your free setup. Please contact our team and we'll get it sorted.";
      }
      const order = (
        await this.$store.dispatch("billing/orderBasket", {
          userId: this.payload.userId,
          siteId,
          productId,
          productTermId,
          paymentMethodId: null,
          couponId: this.payload.couponId,
          currency: this.payload.currency
        })
      ).data;
      return order.orderNum;
    },
    async addTask({ siteId, taskTypePath }) {
      const fqdn = this.payload.siteUrl.fqdn;
      const subject = `Google website cleanup – ${fqdn}`;
      const details = [
        `Hi! Welcome to fixed.net 👋`,
        ``,
        `This task was created automatically as part of the Google × fixed.net website cleanup programme. Your free task coupon has been applied, and our team will begin looking at ${fqdn} shortly.`,
        ``,
        `To help us get started as quickly as possible, please reply to this task with:`,
        ``,
        `- A short description of the problem (for example, the warning Google showed you or anything you've noticed on your site)`,
        `- Login details for your website and hosting (you can also add these securely via your site's "Info" tab)`,
        `- Anything else you think we should know`,
        ``,
        `One of our experts will pick this up and keep you updated right here.`
      ].join("\n");
      const result = await this.$store.dispatch("tasks/addTask", {
        siteId,
        taskTypePath,
        details,
        subject,
        couponId: this.payload.couponId
      });
      return result.data.taskId;
    }
  }
};
</script>

<style lang="scss" scoped>
.provision-list {
  list-style: none;

  li {
    display: flex;
    align-items: center;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    margin-right: 0.5rem;

    .loader {
      height: 1.25rem;
      width: 1.25rem;
    }
  }
}
</style>
